<template>
  <div
    :aria-label="carouselAriaLabel"
    class="md:flex md:flex-col md:gap-8 lg:flex-row-reverse"
    role="region"
  >
    <GalleryDesktopMainPanel>
      <GalleryItemRenderer
        v-if="currentItem"
        :image-aria-label="mainImageAriaLabel"
        :item="currentItem"
        :review-aria-label
        :video-aria-label
        @image-click="emit('image-click', $event)"
        @review-click="handleReviewClick($event)"
        @review-visible="
          emit('review-visible', $event, currentActiveIndex, items.length)
        "
        @video-click="emit('video-click', $event)"
        @video-start="onVideoPlay"
      />
      <div
        v-if="withSticker && currentActiveIndex === 0"
        class="absolute right-8 top-8"
        role="button"
        tabindex="0"
        @click="handleStickerClick"
        @keypress.enter="handleStickerClick"
      >
        <slot name="sticker" />
      </div>
    </GalleryDesktopMainPanel>

    <GalleryDesktopThumblist
      @scroll-end="onScrollEnd"
      @scroll-start="onScrollStart"
    >
      <template
        v-for="(item, index) in items"
        :key="getItemKey(item, index, 'desktop')"
      >
        <GalleryDesktopThumbnailVideo
          v-if="item.kind === 'video'"
          :aria-label="videoThumbnailAriaLabel"
          :is-active="index === currentActiveIndex"
          @select="onThumbnailSelect(index)"
        >
          <GalleryItemRenderer is-static :item />
        </GalleryDesktopThumbnailVideo>

        <GalleryDesktopThumbnailReview
          v-else-if="item.kind === 'review'"
          :aria-label="reviewThumbnailAriaLabel"
          :is-active="index === currentActiveIndex"
          @select="onThumbnailSelect(index)"
        >
          <GalleryItemRenderer is-static :item />
        </GalleryDesktopThumbnailReview>

        <GalleryDesktopThumbnailReview
          v-else-if="item.kind === 'skeleton'"
          :is-active="index === currentActiveIndex"
          @select="onThumbnailSelect(index)"
        >
          <GalleryItemRenderer is-static :item />
        </GalleryDesktopThumbnailReview>

        <GalleryDesktopThumbnail
          v-else
          :aria-label="getThumbnailAriaLabel(index)"
          :is-active="index === currentActiveIndex"
          :with-sticker="withSticker && index === 0"
          @select="onThumbnailSelect(index)"
        >
          <GalleryItemRenderer is-static :item />
          <template #sticker="{ size }">
            <slot name="sticker" :size />
          </template>
        </GalleryDesktopThumbnail>
      </template>
    </GalleryDesktopThumblist>
  </div>
</template>

<script setup lang="ts">
import { type VNode, computed, onMounted } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import translations from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.translations'
import type {
  ProductDetailPageGalleryImage,
  ProductDetailPageGalleryItem,
  ProductDetailPageGalleryVideo,
} from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.types'
import GalleryDesktopMainPanel from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryDesktop/components/GalleryDesktopMainPanel/GalleryDesktopMainPanel.vue'
import GalleryDesktopThumblist from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryDesktop/components/GalleryDesktopThumblist/GalleryDesktopThumblist.vue'
import GalleryDesktopThumbnail from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryDesktop/components/GalleryDesktopThumbnail/GalleryDesktopThumbnail.vue'
import GalleryDesktopThumbnailReview from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryDesktop/components/GalleryDesktopThumbnailReview/GalleryDesktopThumbnailReview.vue'
import GalleryDesktopThumbnailVideo from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryDesktop/components/GalleryDesktopThumbnailVideo/GalleryDesktopThumbnailVideo.vue'
import GalleryItemRenderer from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryItemRenderer.vue'
import { useGalleryTracking } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/composables/useGalleryTracking/useGalleryTracking'
import { getItemKey } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/utils/getItemKey'

const props = defineProps<{
  items: ProductDetailPageGalleryItem[]
  currentActiveIndex: number
  trackingProductModel?: string
  productName?: string
  withSticker?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:current-active-index', index: number): void
  (e: 'image-click', image: ProductDetailPageGalleryImage): void
  (e: 'review-click', review: ReviewWithCount): void
  (
    e: 'review-visible',
    review: ReviewWithCount,
    index: number,
    total: number,
  ): void
  (e: 'video-click', video: ProductDetailPageGalleryVideo): void
  (e: 'sticker-click'): void
}>()

defineSlots<{
  sticker?: (props: { size?: 'small' | 'default' }) => VNode[]
}>()

const i18n = useI18n()

const {
  onScrollStart,
  onScrollEnd,
  onSlideImpression,
  onVideoPlay,
  onReviewClick,
} = useGalleryTracking(props.trackingProductModel ?? '')

const currentItem = computed(() => props.items[props.currentActiveIndex])

const imageTotal = computed(
  () => props.items.filter((item) => item.kind === 'image').length,
)

const carouselAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.carouselAriaLabel, {
    productName: props.productName,
  })
})

const mainImageAriaLabel = computed(() => {
  if (!props.productName || currentItem.value?.kind !== 'image')
    return undefined

  return i18n(translations.mainImageButtonAriaLabel, {
    productName: props.productName,
  })
})

const videoAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.videoButtonAriaLabel, {
    productName: props.productName,
  })
})

const reviewAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.reviewButtonAriaLabel, {
    productName: props.productName,
  })
})

const videoThumbnailAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.videoThumbnailButtonAriaLabel, {
    productName: props.productName,
  })
})

const reviewThumbnailAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.reviewThumbnailButtonAriaLabel, {
    productName: props.productName,
  })
})

function getThumbnailAriaLabel(overallIndex: number): string | undefined {
  if (!props.productName) return undefined

  const item = props.items[overallIndex]
  if (item?.kind !== 'image') return undefined

  let imageIndex = 0
  for (let i = 0; i <= overallIndex; i += 1) {
    if (props.items[i].kind === 'image') imageIndex += 1
  }

  return i18n(translations.thumbnailButtonAriaLabel, {
    productName: props.productName,
    current: imageIndex,
    total: imageTotal.value,
  })
}

// --- Slide impression tracking (fire once per slide) ---
const impressedSlides = new Set<number>()

function trackSlideImpressionOnce(index: number) {
  if (impressedSlides.has(index)) return

  impressedSlides.add(index)
  onSlideImpression({
    currentSlide: index,
    totalSlides: props.items.length,
    itemKind: props.items[index].kind,
  })
}

// Track the initially visible slide on mount
onMounted(() => {
  trackSlideImpressionOnce(props.currentActiveIndex)
})

// --- Thumbnail selection ---
function onThumbnailSelect(index: number) {
  trackSlideImpressionOnce(index)
  emit('update:current-active-index', index)
}

// --- Review click tracking ---
function handleReviewClick(review: ReviewWithCount) {
  onReviewClick({ averageRate: review.averageRate, count: review.count })
  emit('review-click', review)
}

function handleStickerClick(e: Event) {
  e.stopPropagation()
  e.preventDefault()

  emit('sticker-click')
}
</script>
